import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';
import { Checkbox } from '../../../components/ui/Checkbox';
import Select from '../../../components/ui/Select';
import { t } from '../../../utils/translations';
import { formatShortDate } from '../../../utils/dateUtils';


const ArticleTable = ({ 
  articles, 
  selectedArticles, 
  onSelectionChange, 
  onEdit, 
  onPreview, 
  onDuplicate, 
  onStatusToggle,
  onSort,
  sortConfig 
}) => {
  const [viewDensity, setViewDensity] = useState('comfortable'); // compact, comfortable, spacious

  const getStatusBadge = (status) => {
    const statusConfig = {
      published: { 
        color: 'bg-success text-success-foreground', 
        icon: 'CheckCircle',
        label: t('articles.published') 
      },
      draft: { 
        color: 'bg-muted text-muted-foreground', 
        icon: 'Edit3',
        label: t('articles.draft') 
      },
      scheduled: { 
        color: 'bg-warning text-warning-foreground', 
        icon: 'Clock',
        label: t('articles.scheduled') 
      }
    };

    const config = statusConfig?.[status] || statusConfig?.draft;

    return (
      <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${config?.color}`}>
        <Icon name={config?.icon} size={12} />
        <span>{config?.label}</span>
      </span>
    );
  };

  const getCategoryBadge = (category) => {
    const categoryConfig = {
      news: { color: 'bg-accent text-accent-foreground', label: t('articles.news') },
      events: { color: 'bg-secondary text-secondary-foreground', label: t('articles.events') },
      announcements: { color: 'bg-primary text-primary-foreground', label: t('articles.announcements') }
    };

    const config = categoryConfig?.[category] || categoryConfig?.news;

    return (
      <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${config?.color}`}>
        {config?.label}
      </span>
    );
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      onSelectionChange(articles?.map(article => article?.id));
    } else {
      onSelectionChange([]);
    }
  };

  const handleSelectArticle = (articleId, checked) => {
    if (checked) {
      onSelectionChange([...selectedArticles, articleId]);
    } else {
      onSelectionChange(selectedArticles?.filter(id => id !== articleId));
    }
  };

  const isAllSelected = articles?.length > 0 && selectedArticles?.length === articles?.length;
  const isPartiallySelected = selectedArticles?.length > 0 && selectedArticles?.length < articles?.length;

  const getSortIcon = (column) => {
    if (sortConfig?.key !== column) return 'ArrowUpDown';
    return sortConfig?.direction === 'asc' ? 'ArrowUp' : 'ArrowDown';
  };

  const densityClasses = {
    compact: 'py-2',
    comfortable: 'py-3',
    spacious: 'py-4'
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-1 overflow-hidden">
      {/* Table Header */}
      <div className="flex items-center justify-between p-4 border-b border-border bg-muted/30">
        <div className="flex items-center space-x-4">
          <Checkbox
            checked={isAllSelected}
            indeterminate={isPartiallySelected}
            onChange={(e) => handleSelectAll(e?.target?.checked)}
            label={t('common.selectAll').replace('{count}', articles?.length).replace('{items}', t('articles.articles'))}
          />
        </div>

        <div className="flex items-center space-x-2 space-x-reverse">
          <span className="text-sm text-muted-foreground">
            {selectedArticles?.length > 0 && `${selectedArticles?.length} ${t('common.selected')}`}
          </span>
          
          <Select
            options={[
              { value: 'compact', label: t('articles.compact') },
              { value: 'comfortable', label: t('articles.comfortable') },
              { value: 'spacious', label: t('articles.spacious') }
            ]}
            value={viewDensity}
            onChange={setViewDensity}
            className="w-32"
          />
        </div>
      </div>
      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/20">
            <tr>
              <th className="w-12 px-4 py-3"></th>
              <th className="text-right px-4 py-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onSort('title')}
                  iconName={getSortIcon('title')}
                  iconPosition="left"
                  iconSize={14}
                  className="font-medium"
                >
                  {t('common.title')}
                </Button>
              </th>
              <th className="text-right px-4 py-3">{t('common.category')}</th>
              <th className="text-right px-4 py-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onSort('author')}
                  iconName={getSortIcon('author')}
                  iconPosition="left"
                  iconSize={14}
                  className="font-medium"
                >
                  {t('common.author')}
                </Button>
              </th>
              <th className="text-right px-4 py-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onSort('publishedAt')}
                  iconName={getSortIcon('publishedAt')}
                  iconPosition="left"
                  iconSize={14}
                  className="font-medium"
                >
                  {t('articles.published')}
                </Button>
              </th>
              <th className="text-right px-4 py-3">{t('common.status')}</th>
              <th className="text-left px-4 py-3">{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {articles?.map((article) => (
              <tr 
                key={article?.id} 
                className="border-b border-border hover:bg-muted/30 transition-colors"
              >
                <td className="px-4">
                  <Checkbox
                    checked={selectedArticles?.includes(article?.id)}
                    onChange={(e) => handleSelectArticle(article?.id, e?.target?.checked)}
                  />
                </td>
                <td className={`px-4 ${densityClasses?.[viewDensity]}`}>
                  <div className="flex items-center space-x-3 space-x-reverse">
                    {article?.featuredImage && (
                      <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0">
                        <Image
                          src={article?.featuredImage}
                          alt={article?.featuredImageAlt}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <div className="min-w-0">
                      <h4 className="font-medium text-foreground truncate">
                        {article?.title}
                      </h4>
                      <p className="text-sm text-muted-foreground truncate">
                        {article?.excerpt}
                      </p>
                    </div>
                  </div>
                </td>
                <td className={`px-4 ${densityClasses?.[viewDensity]}`}>
                  {getCategoryBadge(article?.category)}
                </td>
                <td className={`px-4 ${densityClasses?.[viewDensity]}`}>
                  <div className="flex items-center space-x-2 space-x-reverse">
                    <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                      <span className="text-xs text-primary-foreground font-medium">
                        {article?.author?.charAt(0)?.toUpperCase()}
                      </span>
                    </div>
                    <span className="text-sm text-foreground">{article?.author}</span>
                  </div>
                </td>
                <td className={`px-4 ${densityClasses?.[viewDensity]}`}>
                  <span className="text-sm text-muted-foreground">
                    {formatShortDate(article?.publishedAt)}
                  </span>
                </td>
                <td className={`px-4 ${densityClasses?.[viewDensity]}`}>
                  {getStatusBadge(article?.status)}
                </td>
                <td className={`px-4 ${densityClasses?.[viewDensity]}`}>
                  <div className="flex items-center justify-start space-x-1 space-x-reverse">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onPreview(article)}
                      iconName="Eye"
                      iconSize={16}
                      title={t('common.preview')}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onEdit(article)}
                      iconName="Edit3"
                      iconSize={16}
                      title={t('common.edit')}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onDuplicate(article)}
                      iconName="Copy"
                      iconSize={16}
                      title={t('common.duplicate')}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onStatusToggle(article)}
                      iconName={article?.status === 'published' ? 'EyeOff' : 'Eye'}
                      iconSize={16}
                      title={article?.status === 'published' ? t('articles.unpublish') : t('articles.publish')}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile Card Layout */}
      <div className="lg:hidden space-y-4 p-4">
        {articles?.map((article) => (
          <div 
            key={article?.id}
            className="border border-border rounded-lg p-4 bg-card hover:bg-muted/30 transition-colors"
          >
            <div className="flex items-start space-x-3">
              <Checkbox
                checked={selectedArticles?.includes(article?.id)}
                onChange={(e) => handleSelectArticle(article?.id, e?.target?.checked)}
                className="mt-1"
              />
              
              {article?.featuredImage && (
                <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                  <Image
                    src={article?.featuredImage}
                    alt={article?.featuredImageAlt}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between mb-2">
                  <h4 className="font-medium text-foreground truncate pr-2">
                    {article?.title}
                  </h4>
                  {getStatusBadge(article?.status)}
                </div>
                
                <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                  {article?.excerpt}
                </p>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    {getCategoryBadge(article?.category)}
                    <span className="text-xs text-muted-foreground">
                      {formatShortDate(article?.publishedAt)}
                    </span>
                  </div>
                  
                  <div className="flex items-center space-x-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onPreview(article)}
                      iconName="Eye"
                      iconSize={16}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onEdit(article)}
                      iconName="Edit3"
                      iconSize={16}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Empty State */}
      {articles?.length === 0 && (
        <div className="text-center py-12">
          <Icon name="FileText" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">{t('articles.noArticlesFound')}</h3>
          <p className="text-muted-foreground mb-4">
            {t('articles.noArticlesDescription')}
          </p>
          <Button
            variant="default"
            onClick={() => window.location.href = '/article-editor'}
            iconName="Plus"
            iconPosition="left"
          >
            {t('articles.createArticle')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default ArticleTable;